import { api } from '../console/api.ts';
import { carrierName } from '../console/carriers.ts';
import { empty, field, pageHead, pill } from '../console/components.ts';
import {
  agentName,
  CURRENCY_NAME,
  type Cur,
  cap,
  date,
  esc,
  fmt,
  guatemalaMonth,
  icon,
  lineName,
  type Money,
  PRODUCT,
  period,
} from '../console/format.ts';
import { type Actions, render } from '../console/router.ts';
import { me, supervises } from '../console/session.ts';
import { $ } from '../console/ui.ts';
import { dateControl, monthControl, optionTag, selectControl } from '../controls.ts';

/**
 * «Mis comisiones» (an agent) and «Comisiones» (management, one agent at a time): each commission
 * policy by policy, accrued, ready to invoice or settled, and the history of the settlements.
 * Each line explains itself (#316): base × rate = the agency's commission by the agreement's
 * rule, × the agent's rate = the agent's share. Management also sees the agency («Agencia»):
 * the month's totals, agreed vs. received per policy, the projection and the agreements in force.
 */

type Standing = 'ACCRUED' | 'READY_TO_INVOICE' | 'SETTLED';
type MyCommissionLine = {
  accrualId: string;
  carrier: string;
  policyNumber: string;
  installment: number | null;
  kind: string;
  date: string;
  standing: Standing;
  disputed: boolean;
  base: Money;
  rate: string | null;
  agencyCommission: Money;
  rule: string | null;
  endorsementRef: string | null;
  settlement: 'PAYABLE' | 'RETAINED';
  agentRate: string | null;
  agentShare: Money | null;
  termsFrom: string | null;
  amount: Money | null;
  settlementPeriod: string | null;
  policyholder: string | null;
  productLine: string;
  productName: string;
  familyName: string | null;
};
type MyCommissions = {
  agentId: string;
  lines: MyCommissionLine[];
  totals: { standing: Standing; currency: Cur; lines: number; amount: Money }[];
  settlements: {
    id: string;
    period: string;
    state: string;
    lines: number;
    commission: Money;
    vat: Money;
    isr: Money;
    payout: Money;
    paid: { reference: string; on: string } | null;
  }[];
};

/** «Acumulada», «Lista para facturar», «Liquidada»: the label and the pill's tone. */
const STANDING: Record<Standing, { label: string; tone: string; icon: string; foot: string }> = {
  ACCRUED: {
    label: 'Acumulada',
    tone: 'accrued',
    icon: 'trend',
    foot: 'Devengada; la aseguradora aún no la confirma',
  },
  READY_TO_INVOICE: {
    label: 'Lista para facturar',
    tone: 'info',
    icon: 'receipt',
    foot: 'Confirmada; entra o ya está en la liquidación',
  },
  SETTLED: { label: 'Liquidada', tone: 'ok', icon: 'ok', foot: 'Pagada en la liquidación' },
};
const FAMILY: Record<string, string> = {
  AUTO: 'Autos',
  PROPERTY: 'Incendio y hogar',
  LIFE: 'Vida',
  HEALTH: 'Gastos médicos',
};
const COMMISSION_KIND: Record<string, string> = {
  COMMISSION: 'Comisión',
  CLAWBACK: 'Reversión',
  ADJUSTMENT: 'Ajuste',
};
/** The line's type as the agency says it: an adjustment says where it comes from. */
const kindLabel = (l: Pick<MyCommissionLine, 'kind' | 'rule'>) =>
  l.kind === 'ADJUSTMENT'
    ? l.rule === 'DISPUTE_RESOLUTION'
      ? 'Ajuste por disputa'
      : 'Ajuste por endoso'
    : (COMMISSION_KIND[l.kind] ?? l.kind);
/** The agreement's rule that produced a line, in the agency's words. */
const RULE: Record<string, string> = {
  NET_SHARE_OF_INSTALLMENT: 'parte neta del recibo × tasa',
  NET_SPLIT_TIMES_RATE: 'prima neta ÷ recibos × tasa',
  ANNUAL_SPLIT: 'comisión anual ÷ recibos',
  COLLECTED_MINUS_EARNED: 'prima cobrada no devengada × tasa',
  AS_STATED_ON_CANCELLATION: 'la que declara la aseguradora al cancelar',
  AS_STATED_CAPPED_AT_RETAINED: 'la que declara la aseguradora, hasta lo ya retenido',
  ENDORSEMENT: 'prima neta del endoso × tasa',
  DISPUTE_RESOLUTION: 'la diferencia aceptada de una disputa',
};
const pct = (r: string) => `${r} %`;
const SETTLEMENT_STATE: Record<string, string> = {
  PREPARED: 'Preparada',
  APPROVED: 'Aprobada',
  PAID: 'Pagada',
  REJECTED: 'Rechazada',
};
/** What «Mis comisiones» shows (the agent is the person's own unless management picks one). */
let commissionsFilter = {
  agent: '',
  from: '',
  to: '',
  standing: '',
  family: '',
  productLine: '',
};

function commissionsQuery() {
  const p = new URLSearchParams();
  for (const k of ['from', 'to', 'standing', 'family', 'productLine'] as const)
    if (commissionsFilter[k]) p.set(k, commissionsFilter[k]);
  const s = p.toString();
  return s ? `?${s}` : '';
}

function commissionsFilters(agents: string[], agent: string) {
  const f = commissionsFilter;
  const opt = optionTag;
  // PROPERTY is a ramo (family), not a product line.
  const lines = Object.keys(PRODUCT).filter((l) => l !== 'PROPERTY');
  return `<form class="card toolbar stats-filters" id="commissions-filters" aria-label="Filtros de las comisiones">
    ${agents.length > 1 ? field('Agente', selectControl({ id: 'cm-agent' }, agents.map((a) => opt(a, agentName(a), a === agent)).join(''))) : ''}
    ${field('Desde', dateControl({ id: 'cm-from', value: f.from }))}
    ${field('Hasta', dateControl({ id: 'cm-to', value: f.to }))}
    ${field('Estado', selectControl({ id: 'cm-standing' }, `${opt('', 'Todos', !f.standing)}${(Object.keys(STANDING) as Standing[]).map((s) => opt(s, STANDING[s].label, s === f.standing)).join('')}`))}
    ${field(
      'Ramo',
      selectControl(
        { id: 'cm-family' },
        `${opt('', 'Todos', !f.family)}${Object.entries(FAMILY)
          .map(([k, v]) => opt(k, v, k === f.family))
          .join('')}`,
      ),
    )}
    ${field('Producto', selectControl({ id: 'cm-line' }, `${opt('', 'Todos', !f.productLine)}${lines.map((l) => opt(l, lineName(l), l === f.productLine)).join('')}`))}
    <p class="muted small stats-scope">${icon(agents.length > 1 ? 'users' : 'lock')}${agents.length > 1 ? `Comisiones de ${esc(agentName(agent))}` : 'Solo tus comisiones'}</p>
  </form>`;
}

/** Totals per standing, one row of tiles per currency (quetzales and dollars never added). */
function commissionTiles(r: MyCommissions) {
  const currencies = [...new Set(r.totals.map((t) => t.currency))];
  const multi = currencies.length > 1;
  return currencies
    .map((cur) => {
      const label = cap(CURRENCY_NAME[cur]);
      const tiles = r.totals
        .filter((t) => t.currency === cur)
        .map((t) => {
          const s = STANDING[t.standing];
          return `<div class="card kpi"><span class="kpi-label">${icon(s.icon)}${esc(s.label)}</span><span class="kpi-value">${fmt(t.amount)}</span><span class="kpi-foot">${t.lines === 1 ? '1 comisión' : `${t.lines} comisiones`} · ${esc(s.foot)}</span></div>`;
        })
        .join('');
      return `${multi ? `<h2 class="kpi-group">${esc(label)}</h2>` : ''}<section class="kpis commission-kpis" data-component="commission-kpis" aria-label="${esc(`Totales por estado en ${CURRENCY_NAME[cur]}`)}">${tiles}</section>`;
    })
    .join('');
}

export async function comisiones() {
  const management = supervises();
  const agents = management
    ? (await api<{ agentId: string }[]>('GET', '/v1/agents')).map((a) => a.agentId)
    : me?.agentId
      ? [me.agentId]
      : [];
  const agent = agents.includes(commissionsFilter.agent)
    ? commissionsFilter.agent
    : (agents[0] ?? '');
  const head = pageHead(
    management ? 'Comisiones de los agentes' : 'Mis comisiones',
    management
      ? 'La comisión de cada agente, póliza por póliza: acumulada, lista para facturar o liquidada, con el historial de sus liquidaciones.'
      : 'Tu parte de cada comisión, póliza por póliza: <strong>acumulada</strong> mientras la aseguradora la confirma, <strong>lista para facturar</strong> cuando la confirmó y <strong>liquidada</strong> cuando se te pagó.',
  );
  if (management && commissionsTab === 'agency')
    return `${head}${commissionTabs()}${await agencyPanel()}`;
  if (!agent)
    return `${head}${empty('settle', 'No hay agentes', 'Cuando la agencia tenga agentes con comisiones, aparecerán aquí.', '', 2)}`;
  const r = await api<MyCommissions>(
    'GET',
    `/v1/agents/${encodeURIComponent(agent)}/commissions${commissionsQuery()}`,
  );
  const filtered = Object.entries(commissionsFilter).some(([k, v]) => k !== 'agent' && v);
  const rows = r.lines.map((l) => commissionRow(l, management)).join('');
  const detail = r.lines.length
    ? `<section class="card"><div class="card-head"><h2>Detalle por póliza</h2><span class="spacer"></span><span class="muted small">${r.lines.length === 1 ? '1 comisión' : `${r.lines.length} comisiones`}, de la más reciente a la más antigua</span></div>
      <div class="table-wrap" data-component="table"><table class="table commission-table"><caption class="sr-only">Comisiones por póliza</caption><thead><tr><th>Fecha</th><th>Póliza</th><th>Cliente</th><th>Ramo</th><th class="num">Base × tasa</th><th class="num wrap">Comisión de la agencia</th><th>Estado</th><th class="num">${management ? 'Comisión del agente' : 'Tu comisión'}</th></tr></thead>
      <tbody>${rows}</tbody></table></div></section>`
    : empty(
        'settle',
        filtered ? 'Ninguna comisión con estos filtros' : 'Todavía no hay comisiones',
        filtered
          ? 'Cambia las fechas, el estado o el ramo para ver más.'
          : 'Cuando la aseguradora reciba el pago de una póliza, su comisión aparecerá aquí como acumulada.',
        '',
        2,
      );
  const history = r.settlements.length
    ? `<section class="card"><div class="card-head"><h2>Liquidaciones y pagos</h2></div><div class="table-wrap" data-component="table"><table class="table commission-table"><caption class="sr-only">Historial de liquidaciones</caption>
      <thead><tr><th>Período</th><th>Estado</th><th class="num">Comisión</th><th class="num">IVA</th><th class="num">ISR retenido</th><th class="num">A pagar</th><th>Pago</th></tr></thead><tbody>
      ${r.settlements
        .map(
          (
            s,
          ) => `<tr><td>${esc(period(s.period))}</td><td>${pill(s.state, SETTLEMENT_STATE[s.state] ?? s.state)}</td>
          <td class="num">${fmt(s.commission)}</td><td class="num">${fmt(s.vat)}</td><td class="num">${fmt(s.isr)}</td><td class="num"><strong>${fmt(s.payout)}</strong></td>
          <td>${s.paid ? `<span class="mono">${esc(s.paid.reference)}</span><div class="muted small">${esc(date(s.paid.on))}</div>` : '<span class="muted">Sin pagar</span>'}</td></tr>`,
        )
        .join('')}</tbody></table></div></section>`
    : `<section class="card"><div class="card-head"><h2>Liquidaciones y pagos</h2></div><p class="muted card-pad">Aún no hay liquidaciones${filtered ? ' en este rango' : ''}.</p></section>`;
  const body = `${commissionsFilters(agents, agent)}${commissionTiles(r)}${detail}${history}`;
  return management
    ? `${head}${commissionTabs()}<div id="commissions-panel" class="commissions-panel" role="tabpanel" aria-labelledby="cmt-agents">${body}</div>`
    : `${head}${body}`;
}

/** One commission: its numbers in the row, and how it was computed in the row under it. */
function commissionRow(l: MyCommissionLine, management: boolean) {
  const s = STANDING[l.standing];
  const id = esc(l.accrualId);
  const what = `${kindLabel(l)}${l.installment ? ` · recibo ${l.installment}` : ''}`;
  const negative = l.agencyCommission.amountMinor < 0;
  return `<tr class="${negative ? 'is-reversal' : ''}"><td class="nowrap">${esc(date(l.date))}</td>
      <td><span class="mono">${esc(l.policyNumber)}</span><div class="muted small">${esc(carrierName(l.carrier))} · ${esc(what)}</div></td>
      <td>${l.policyholder ? esc(l.policyholder) : '<span class="muted">—</span>'}</td>
      <td>${esc(l.familyName ?? l.productName)}<div class="muted small">${esc(l.productName)}</div></td>
      <td class="num nowrap">${l.rate && l.base.amountMinor ? `${fmt(l.base)} <span class="muted">×</span> ${esc(pct(l.rate))}` : '<span class="muted small">Declarada</span>'}</td>
      <td class="num">${fmt(l.agencyCommission)}</td>
      <td><div class="chips">${pill(s.tone, s.label)}${l.disputed ? pill('warn', 'En disputa') : ''}${l.settlement === 'RETAINED' && l.kind === 'COMMISSION' ? pill('info', 'Retenida') : ''}</div></td>
      <td class="num"><strong>${l.amount ? fmt(l.amount) : '<span class="muted small">Sin tasa para el ramo</span>'}</strong>
        <div><button type="button" class="btn sm calc-toggle" data-calc-toggle="${id}" aria-expanded="false" aria-controls="calc-${id}">Ver cálculo</button></div></td></tr>
      <tr class="calc-row" id="calc-${id}" hidden><td colspan="8">${explanation(l, management)}</td></tr>`;
}

/**
 * «Q1,200.00 de prima neta × 10 % = Q120.00; parte del agente 70 % = Q84.00», with the rule of
 * the agreement and the version of the agent's terms used.
 */
function explanation(l: MyCommissionLine, management: boolean) {
  const whose = management ? 'Parte del agente' : 'Tu parte';
  const agency =
    l.rate && l.base.amountMinor
      ? `${fmt(l.base)} de prima neta × ${esc(pct(l.rate))} = <strong>${fmt(l.agencyCommission)}</strong> de comisión de la agencia`
      : `<strong>${fmt(l.agencyCommission)}</strong> de comisión de la agencia, como la declara la aseguradora`;
  const share =
    l.agentRate && l.agentShare
      ? `${whose}: ${esc(pct(l.agentRate))} de ${fmt(l.agencyCommission)} = <strong>${fmt(l.agentShare)}</strong>`
      : `${whose}: sin tasa pactada para el ramo ${esc(l.productName)}; no se liquida hasta que se pacte.`;
  const facts = [
    l.rule
      ? `Regla del convenio con ${esc(carrierName(l.carrier))}: ${esc(RULE[l.rule] ?? l.rule)}.`
      : '',
    l.termsFrom
      ? `Términos del agente vigentes desde el ${esc(date(l.termsFrom))}.`
      : 'Términos actuales del agente.',
    l.endorsementRef ? `Viene del endoso <span class="mono">${esc(l.endorsementRef)}</span>.` : '',
    l.kind === 'CLAWBACK'
      ? 'Reversión por la cancelación de la póliza: resta de las comisiones.'
      : '',
    l.settlement === 'RETAINED' && l.kind === 'COMMISSION'
      ? 'Retenida: la agencia ya se quedó con ella al remesar la prima neta de comisión.'
      : '',
    l.settlementPeriod && l.amount
      ? `Quedó en la liquidación de ${esc(period(l.settlementPeriod))}: ${fmt(l.amount)}.`
      : '',
  ].filter(Boolean);
  return `<div class="calc" data-component="commission-calc"><p>${agency}</p><p>${share}</p><ul class="muted small">${facts.map((f) => `<li>${f}</li>`).join('')}</ul></div>`;
}

// ------------------------------------------------------------------ the agency (management)

type CurrencyAmount = { currency: Cur; amount: Money };
type Overview = {
  period: string;
  totals: {
    accrued: CurrencyAmount[];
    reversed: CurrencyAmount[];
    retainedByAgency: CurrencyAmount[];
    paid: Money;
    payable: Money;
    isr: Money;
  };
  agreedVsReceived: {
    carrier: string;
    policyNumber: string;
    currency: Cur;
    agreed: Money;
    received: Money | null;
    difference: Money | null;
  }[];
};
type Projection = {
  months: number;
  byMonth: { period: string; currency: Cur; receipts: number; commission: Money }[];
  totals: { currency: Cur; receipts: number; commission: Money }[];
};
type Agreements = {
  on: string;
  carriers: {
    carrier: string;
    agreement: {
      perInstallment: { strategy: string };
      clawback: { strategy: string };
      withholding: { rate: { value: string } } | null;
      settlement: 'PAYABLE' | 'RETAINED';
    } | null;
    rates: {
      plan: string;
      productLine: string;
      rate: string;
      effectiveFrom: string;
      renewalRate: string | null;
    }[];
  }[];
  agents: {
    agentId: string;
    split: string;
    splits: Record<string, string> | null;
    since: string | null;
  }[];
};

/** «Por agente» (each agent's lines) or «Agencia» (the agency's month, projection, agreements). */
let commissionsTab: 'agents' | 'agency' = 'agents';
let agencyMonth = '';
let projectionMonths = '6';

function commissionTabs() {
  const tab = (k: 'agents' | 'agency', label: string) =>
    `<button type="button" class="btn sm" role="tab" id="cmt-${k}" aria-selected="${commissionsTab === k}" aria-pressed="${commissionsTab === k}" aria-controls="commissions-panel" data-cm-tab="${k}">${esc(label)}</button>`;
  return `<div class="card toolbar"><div class="lead-filters" role="tablist" aria-label="Qué comisiones ver">${tab('agents', 'Por agente')}${tab('agency', 'Agencia')}</div></div>`;
}

const amounts = (xs: CurrencyAmount[]) =>
  xs
    .filter((x) => x.currency === 'GTQ' || x.amount.amountMinor !== 0)
    .map((x) => fmt(x.amount))
    .join(' · ');

async function agencyPanel() {
  const month = agencyMonth || guatemalaMonth(new Date());
  const [o, p, a] = await Promise.all([
    api<Overview>('GET', `/v1/commissions/overview?period=${encodeURIComponent(month)}`),
    api<Projection>(
      'GET',
      `/v1/commissions/projection?months=${encodeURIComponent(projectionMonths)}`,
    ),
    api<Agreements>('GET', '/v1/commissions/agreements'),
  ]);
  const kpi = (ic: string, label: string, value: string, foot: string) =>
    `<div class="card kpi"><span class="kpi-label">${icon(ic)}${esc(label)}</span><span class="kpi-value">${value}</span><span class="kpi-foot">${esc(foot)}</span></div>`;
  const totals = `<section class="kpis agency-commission-kpis" data-component="agency-commission-kpis" aria-label="${esc(`Totales de ${period(month)}`)}">
    ${kpi('trend', 'Devengado', amounts(o.totals.accrued), 'Comisiones y ajustes del mes')}
    ${kpi('ok', 'Pagado', fmt(o.totals.paid), 'Liquidaciones pagadas a los agentes')}
    ${kpi('receipt', 'Por pagar', fmt(o.totals.payable), 'Liquidaciones preparadas o aprobadas')}
    ${kpi('lock', 'Retenido (ISR)', fmt(o.totals.isr), 'ISR retenido en las liquidaciones')}
    ${kpi('alert', 'Revertido', amounts(o.totals.reversed), 'Reversiones por cancelaciones')}
  </section>`;
  const compareRows = o.agreedVsReceived
    .map(
      (
        r,
      ) => `<tr><td><span class="mono">${esc(r.policyNumber)}</span><div class="muted small">${esc(carrierName(r.carrier))}</div></td>
      <td class="num">${fmt(r.agreed)}</td><td class="num">${r.received ? fmt(r.received) : '<span class="muted small">Sin estado de cuenta</span>'}</td>
      <td class="num">${r.difference ? (r.difference.amountMinor ? `<strong>${fmt(r.difference)}</strong>` : fmt(r.difference)) : '—'}</td>
      <td>${r.difference === null ? pill('accrued', 'Pendiente') : r.difference.amountMinor === 0 ? pill('ok', 'Coincide') : pill('warn', 'Diferencia')}</td></tr>`,
    )
    .join('');
  const compare = `<section class="card"><div class="card-head"><h2>Pactada vs. recibida</h2><span class="spacer"></span><span class="muted small">Por póliza, en ${esc(period(month))}</span></div>
    ${
      o.agreedVsReceived.length
        ? `<div class="table-wrap" data-component="table"><table class="table commission-table"><caption class="sr-only">Comisión pactada y recibida por póliza</caption>
      <thead><tr><th>Póliza</th><th class="num">Pactada</th><th class="num">Recibida</th><th class="num">Diferencia</th><th>Estado</th></tr></thead><tbody>${compareRows}</tbody></table></div>`
        : `<p class="muted card-pad">No hay comisiones en ${esc(period(month))}.</p>`
    }</section>`;
  const multi = p.totals.length > 1;
  const projRows = p.byMonth
    .filter(
      (m) => m.currency === 'GTQ' || p.totals.some((t) => t.currency === m.currency && t.receipts),
    )
    .map(
      (m) =>
        `<tr><td>${esc(period(m.period))}${multi ? ` <span class="muted small">${esc(CURRENCY_NAME[m.currency])}</span>` : ''}</td><td class="num">${m.receipts}</td><td class="num">${fmt(m.commission)}</td></tr>`,
    )
    .join('');
  const projection = `<section class="card"><div class="card-head"><h2>Proyección de comisiones</h2><span class="spacer"></span>
    ${field('Meses', selectControl({ id: 'cm-proj-months' }, ['3', '6', '12'].map((n) => optionTag(n, `${n} meses`, n === projectionMonths)).join('')))}</div>
    <p class="muted small card-pad">De los recibos pendientes de las pólizas vigentes que vencen desde hoy, con la regla de cada aseguradora.</p>
    <div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Comisión esperada por mes</caption>
    <thead><tr><th>Mes</th><th class="num">Recibos</th><th class="num">Comisión esperada</th></tr></thead><tbody>${projRows}</tbody>
    <tfoot>${p.totals.map((t) => `<tr><td><strong>Total${multi ? ` en ${esc(CURRENCY_NAME[t.currency])}` : ''}</strong></td><td class="num">${t.receipts}</td><td class="num"><strong>${fmt(t.commission)}</strong></td></tr>`).join('')}</tfoot></table></div></section>`;
  const carriers = a.carriers
    .map((c) => {
      const rules = c.agreement
        ? `${esc(RULE[c.agreement.perInstallment.strategy] ?? c.agreement.perInstallment.strategy)}; reversión: ${esc(RULE[c.agreement.clawback.strategy] ?? c.agreement.clawback.strategy)}${c.agreement.withholding ? `; retiene ${esc(pct(c.agreement.withholding.rate.value))}` : ''}${c.agreement.settlement === 'RETAINED' ? '; la agencia retiene la comisión al remesar' : ''}`
        : 'Sin convenio registrado';
      const rates = c.rates.length
        ? c.rates
            .map(
              (r) =>
                `<tr><td>${esc(lineName(r.productLine))} <span class="muted small mono">${esc(r.plan)}</span></td><td class="num">${esc(pct(r.rate))}</td><td class="num">${r.renewalRate ? esc(pct(r.renewalRate)) : '<span class="muted small">No informada</span>'}</td><td>${esc(date(r.effectiveFrom))}</td></tr>`,
            )
            .join('')
        : '<tr><td colspan="4" class="muted">Sin tasas registradas</td></tr>';
      return `<div class="agreement-carrier"><h3>${esc(carrierName(c.carrier))}</h3><p class="muted small">${rules}</p>
      <div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">${esc(`Tasas de ${carrierName(c.carrier)}`)}</caption>
      <thead><tr><th>Ramo y plan</th><th class="num">Primer año</th><th class="num">Renovación</th><th>Vigente desde</th></tr></thead><tbody>${rates}</tbody></table></div></div>`;
    })
    .join('');
  const agentRows = a.agents
    .map(
      (t) =>
        `<tr><td>${esc(agentName(t.agentId))}</td><td>${
          t.splits
            ? Object.entries(t.splits)
                .map(([k, v]) => `${esc(lineName(k))} ${esc(pct(v))}`)
                .join(' · ')
            : esc(pct(t.split))
        }</td><td class="num">${
          t.splits
            ? Object.entries(t.splits)
                .map(([k, v]) => `${esc(lineName(k))} ${esc(pct(String(100 - Number(v))))}`)
                .join(' · ')
            : esc(pct(String(100 - Number(t.split))))
        }</td><td>${t.since ? esc(date(t.since)) : '<span class="muted small">Sin versiones</span>'}</td></tr>`,
    )
    .join('');
  const agreements = `<section class="card"><div class="card-head"><h2>Convenio vigente</h2><span class="spacer"></span><span class="muted small">Al ${esc(date(a.on))}</span></div>
    ${carriers || '<div class="agreement-carrier"><p class="muted">La agencia no trabaja con aseguradoras todavía.</p></div>'}
    <div class="agreement-carrier"><h3>Términos de los agentes</h3>
    <div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Parte de cada agente</caption>
    <thead><tr><th>Agente</th><th>Parte del agente</th><th class="num">Parte de la agencia</th><th>Vigentes desde</th></tr></thead><tbody>${agentRows || '<tr><td colspan="4" class="muted">Sin agentes con términos</td></tr>'}</tbody></table></div></div></section>`;
  return `<div id="commissions-panel" class="commissions-panel" role="tabpanel" aria-labelledby="cmt-agency">
    <form class="card toolbar" id="agency-commissions-filters" aria-label="Mes de las comisiones de la agencia">${field('Mes', monthControl({ id: 'cm-agency-month', value: month }))}</form>
    ${totals}${compare}${projection}${agreements}</div>`;
}

/** Forgets the filters (a new session). */
export function resetCommissions() {
  commissionsFilter = { agent: '', from: '', to: '', standing: '', family: '', productLine: '' };
  commissionsTab = 'agents';
  agencyMonth = '';
  projectionMonths = '6';
}

/** A filter changed: the list is read again, with the focus back on that filter. */
function applyCommissionsFilter(sel: HTMLSelectElement) {
  const key = {
    'cm-agent': 'agent',
    'cm-from': 'from',
    'cm-to': 'to',
    'cm-standing': 'standing',
    'cm-family': 'family',
    'cm-line': 'productLine',
  }[sel.id] as keyof typeof commissionsFilter | undefined;
  if (key) commissionsFilter[key] = sel.value;
  // A range that ends before it starts is read the other way around.
  if (
    commissionsFilter.from &&
    commissionsFilter.to &&
    commissionsFilter.from > commissionsFilter.to
  )
    [commissionsFilter.from, commissionsFilter.to] = [commissionsFilter.to, commissionsFilter.from];
  void render('comisiones', { quiet: true }).then(() => $(`#${sel.id}`)?.focus());
}

/** The month or the projection's length of «Agencia» changed: read again, focus back on it. */
function applyAgencyFilter(sel: HTMLSelectElement) {
  if (sel.id === 'cm-agency-month') agencyMonth = sel.value;
  if (sel.id === 'cm-proj-months') projectionMonths = sel.value;
  void render('comisiones', { quiet: true }).then(() => $(`#${sel.id}`)?.focus());
}

export const actions: Actions = {
  click: {
    /** «Ver cálculo»: shows (or hides) how the line was computed, right under it. */
    calcToggle: (button, d) => {
      const row = $(`#calc-${CSS.escape(d.calcToggle ?? '')}`);
      if (!row) return;
      const open = row.hidden;
      row.hidden = !open;
      button.setAttribute('aria-expanded', String(open));
      button.textContent = open ? 'Ocultar cálculo' : 'Ver cálculo';
    },
    cmTab: (_, d) => {
      const next = d.cmTab === 'agency' ? 'agency' : 'agents';
      commissionsTab = next;
      void render('comisiones', { quiet: true }).then(() => $(`#cmt-${next}`)?.focus());
    },
  },
  submit: {
    'commissions-filters': (ev) => ev.preventDefault(),
    'agency-commissions-filters': (ev) => ev.preventDefault(),
  },
  change: {
    'cm-agent': applyCommissionsFilter,
    'cm-from': applyCommissionsFilter,
    'cm-to': applyCommissionsFilter,
    'cm-standing': applyCommissionsFilter,
    'cm-family': applyCommissionsFilter,
    'cm-line': applyCommissionsFilter,
    'cm-agency-month': applyAgencyFilter,
    'cm-proj-months': applyAgencyFilter,
  },
};
